import { create } from 'zustand';
import { CardColor, CardType } from '@/types/card';
import { Card } from '@/types/card';
import { Team, Player } from '@/types/player';
import { RoundResult } from '@/types/game';
import { useSettingsStore } from './settingsStore';

/**
 * カードホバープレビュー
 * 自分の手札カードにホバーしている時、もし出したらどうなるかを示す。
 * targetPlayerId は保持せず、購読側で gameState から都度計算する（stale 防止）。
 */
export interface CardHoverPreview {
  cardType: CardType;
  /** Draw 系カードの場合の付与枚数（2 or 4） */
  drawAmount?: 2 | 4;
}

/**
 * 画面タイプ
 */
type ScreenType =
  | 'title'
  | 'main_menu'
  | 'solo_setup'
  | 'local_setup'
  | 'online_setup'
  | 'lobby'
  | 'game_board'
  | 'settings'
  | 'game_over';

/**
 * トーストタイプ
 */
type ToastType = 'info' | 'success' | 'warning' | 'error';

/**
 * トースト通知
 */
interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
  timestamp: number;
}

/**
 * アニメーションタイプ
 */
type AnimationType = 'deal_card' | 'play_card' | 'draw_card' | 'deal_cards' | 'particle_effect' | 'win_celebration';

/**
 * アニメーション状態
 */
type AnimationStatus = 'pending' | 'playing' | 'completed';

/**
 * カードプレイアニメーションデータ
 */
interface PlayCardAnimationData {
  cardId: string;
  playerId: string;
  declaredColor?: CardColor;
}

/**
 * カードドローアニメーションデータ
 */
interface DrawCardAnimationData {
  playerId: string;
  card: Card;
}

/**
 * カード配布アニメーションデータ
 */
interface DealCardsAnimationData {
  players: Player[];
  localPlayerId: string;
}

/**
 * アニメーションデータ型
 */
type AnimationData = PlayCardAnimationData | DrawCardAnimationData | DealCardsAnimationData | Record<string, unknown>;

/**
 * アニメーション
 */
interface Animation {
  id: string;
  type: AnimationType;
  data: AnimationData;
  status: AnimationStatus;
}

/**
 * モーダル状態
 */
interface ModalStates {
  colorPicker: {
    isOpen: boolean;
    onColorSelected?: (color: CardColor) => void;
  };
  gameOver: {
    isOpen: boolean;
    winner?: Team;
  };
  pauseMenu: {
    isOpen: boolean;
  };
  settings: {
    isOpen: boolean;
  };
  rules: {
    isOpen: boolean;
  };
  turnTransition: {
    isOpen: boolean;
    nextPlayerName?: string;
    nextPlayerId?: string;
  };
  roundResult: {
    isOpen: boolean;
    result?: RoundResult;
  };
}

/**
 * ローディング状態
 */
interface LoadingStates {
  gameInitialization: boolean;
  networkConnection: boolean;
  assetLoading: boolean;
}

/**
 * ユーザー設定（settingsStoreから同期）
 */
interface UserPreferences {
  soundEnabled: boolean;
  animationsEnabled: boolean;
}

/**
 * UIストア状態
 */
export interface UIStoreState {
  // 画面遷移
  activeScreen: ScreenType;
  previousScreen: ScreenType | null;

  // モーダル
  modals: ModalStates;

  // アニメーションキュー
  animationQueue: Animation[];

  // トースト通知
  toasts: Toast[];

  // ローディング状態
  loadingStates: LoadingStates;

  // ユーザー設定
  preferences: UserPreferences;

  // カードホバープレビュー（自分のターンで自分の手札カードにホバーしている時のみ非null）
  cardHoverPreview: CardHoverPreview | null;

  // アクション: 画面遷移
  navigateTo: (screen: ScreenType) => void;
  goBack: () => void;

  // アクション: モーダル
  openColorPicker: (onColorSelected: (color: CardColor) => void) => void;
  closeColorPicker: () => void;
  openGameOver: (winner: Team) => void;
  closeGameOver: () => void;
  togglePauseMenu: () => void;
  openPauseMenu: () => void;
  closePauseMenu: () => void;
  toggleSettings: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  openRules: () => void;
  closeRules: () => void;
  openTurnTransition: (nextPlayerName: string, nextPlayerId: string) => void;
  closeTurnTransition: () => void;
  confirmTurnTransition: () => void;
  openRoundResult: (result: RoundResult) => void;
  closeRoundResult: () => void;

  // アクション: アニメーション
  queueAnimation: (animation: Omit<Animation, 'id' | 'status'>) => string;
  startAnimation: (animationId: string) => void;
  completeAnimation: (animationId: string) => void;
  clearAnimationQueue: () => void;
  getPendingAnimations: () => Animation[];

  // アクション: トースト
  showToast: (message: string, type: ToastType, duration?: number) => void;
  dismissToast: (toastId: string) => void;

  // アクション: ローディング
  setLoading: (key: keyof LoadingStates, isLoading: boolean) => void;

  // アクション: 設定同期
  syncPreferences: () => void;

  // アクション: 入力ブロック状態取得
  isTransitionBlocking: () => boolean;

  // アクション: カードホバープレビュー
  setCardHoverPreview: (preview: CardHoverPreview | null) => void;
  clearCardHoverPreview: () => void;
}

/**
 * UIストア
 * UI状態、画面遷移、モーダル、アニメーション、通知を管理
 */
export const useUIStore = create<UIStoreState>((set, get) => ({
  // 初期状態
  activeScreen: 'title',
  previousScreen: null,

  modals: {
    colorPicker: {
      isOpen: false,
      onColorSelected: undefined
    },
    gameOver: {
      isOpen: false,
      winner: undefined
    },
    pauseMenu: {
      isOpen: false
    },
    settings: {
      isOpen: false
    },
    rules: {
      isOpen: false
    },
    turnTransition: {
      isOpen: false,
      nextPlayerName: undefined,
      nextPlayerId: undefined
    },
    roundResult: {
      isOpen: false,
      result: undefined
    }
  },

  animationQueue: [],
  toasts: [],

  loadingStates: {
    gameInitialization: false,
    networkConnection: false,
    assetLoading: false
  },

  preferences: {
    soundEnabled: true,
    animationsEnabled: true
  },

  cardHoverPreview: null,

  /**
   * 画面遷移
   * @param screen 遷移先の画面
   */
  navigateTo: (screen: ScreenType) => {
    const currentScreen = get().activeScreen;

    set({
      previousScreen: currentScreen,
      activeScreen: screen
    });

    // 画面固有の初期化
    if (screen === 'game_board') {
      // ゲーム画面に遷移時、アニメーションキューをクリア
      get().clearAnimationQueue();
    } else if (screen === 'main_menu' || screen === 'title') {
      // メインメニュー/タイトルに戻る時、すべてのモーダルを閉じる
      set({
        modals: {
          colorPicker: { isOpen: false },
          gameOver: { isOpen: false },
          pauseMenu: { isOpen: false },
          settings: { isOpen: false },
          rules: { isOpen: false },
          turnTransition: { isOpen: false, nextPlayerName: undefined, nextPlayerId: undefined },
          roundResult: { isOpen: false, result: undefined }
        }
      });
    }

    console.log(`Navigated from ${currentScreen} to ${screen}`);
  },

  /**
   * 前の画面に戻る
   */
  goBack: () => {
    const { previousScreen } = get();
    if (previousScreen) {
      set({
        activeScreen: previousScreen,
        previousScreen: null
      });

      console.log(`Navigated back to ${previousScreen}`);
    } else {
      console.warn('No previous screen to go back to');
    }
  },

  /**
   * 色選択モーダルを開く
   * @param onColorSelected 色選択時のコールバック
   */
  openColorPicker: (onColorSelected: (color: CardColor) => void) => {
    set(state => ({
      modals: {
        ...state.modals,
        colorPicker: {
          isOpen: true,
          onColorSelected
        }
      }
    }));

    console.log('Color picker opened');
  },

  /**
   * 色選択モーダルを閉じる
   */
  closeColorPicker: () => {
    set(state => ({
      modals: {
        ...state.modals,
        colorPicker: {
          isOpen: false,
          onColorSelected: undefined
        }
      }
    }));

    console.log('Color picker closed');
  },

  /**
   * ゲーム終了モーダルを開く
   * @param winner 勝利チーム
   */
  openGameOver: (winner: Team) => {
    set(state => ({
      modals: {
        ...state.modals,
        gameOver: {
          isOpen: true,
          winner
        }
      }
    }));

    console.log(`Game over modal opened - Winner: ${winner}`);
  },

  /**
   * ゲーム終了モーダルを閉じる
   */
  closeGameOver: () => {
    set(state => ({
      modals: {
        ...state.modals,
        gameOver: {
          isOpen: false,
          winner: undefined
        }
      }
    }));

    console.log('Game over modal closed');
  },

  /**
   * ポーズメニューを開閉
   */
  togglePauseMenu: () => {
    set(state => ({
      modals: {
        ...state.modals,
        pauseMenu: {
          isOpen: !state.modals.pauseMenu.isOpen
        }
      }
    }));

    console.log(`Pause menu ${get().modals.pauseMenu.isOpen ? 'opened' : 'closed'}`);
  },

  /**
   * ポーズメニューを開く
   */
  openPauseMenu: () => {
    set((state) => ({
      modals: {
        ...state.modals,
        pauseMenu: { isOpen: true }
      }
    }));

    console.log('Pause menu opened');
  },

  /**
   * ポーズメニューを閉じる
   */
  closePauseMenu: () => {
    set(state => ({
      modals: {
        ...state.modals,
        pauseMenu: { isOpen: false }
      }
    }));

    console.log('Pause menu closed');
  },

  /**
   * 設定モーダルを開閉
   */
  toggleSettings: () => {
    set(state => ({
      modals: {
        ...state.modals,
        settings: {
          isOpen: !state.modals.settings.isOpen
        }
      }
    }));

    console.log(`Settings modal ${get().modals.settings.isOpen ? 'opened' : 'closed'}`);
  },

  /**
   * 設定モーダルを開く
   */
  openSettings: () => {
    set(state => ({
      modals: {
        ...state.modals,
        settings: { isOpen: true }
      }
    }));

    console.log('Settings modal opened');
  },

  /**
   * 設定モーダルを閉じる
   */
  closeSettings: () => {
    set(state => ({
      modals: {
        ...state.modals,
        settings: { isOpen: false }
      }
    }));

    console.log('Settings modal closed');
  },

  /**
   * ルール説明モーダルを開く
   */
  openRules: () => {
    set(state => ({
      modals: {
        ...state.modals,
        rules: { isOpen: true }
      }
    }));

    console.log('Rules modal opened');
  },

  /**
   * ルール説明モーダルを閉じる
   */
  closeRules: () => {
    set(state => ({
      modals: {
        ...state.modals,
        rules: { isOpen: false }
      }
    }));

    console.log('Rules modal closed');
  },

  /**
   * ターン遷移モーダルを開く
   * @param nextPlayerName 次のプレイヤー名
   * @param nextPlayerId 次のプレイヤーID
   */
  openTurnTransition: (nextPlayerName: string, nextPlayerId: string) => {
    set(state => ({
      modals: {
        ...state.modals,
        turnTransition: {
          isOpen: true,
          nextPlayerName,
          nextPlayerId
        }
      }
    }));

    console.log(`Turn transition modal opened - Next player: ${nextPlayerName}`);
  },

  /**
   * ターン遷移モーダルを閉じる
   */
  closeTurnTransition: () => {
    set(state => ({
      modals: {
        ...state.modals,
        turnTransition: {
          isOpen: false,
          nextPlayerName: undefined,
          nextPlayerId: undefined
        }
      }
    }));

    console.log('Turn transition modal closed');
  },

  /**
   * ターン遷移を確認（プレイヤー切替を実行）
   */
  confirmTurnTransition: () => {
    const { modals } = get();
    const nextPlayerId = modals.turnTransition.nextPlayerId;

    if (nextPlayerId) {
      // gameStoreのswitchLocalPlayerを呼び出す
      // Note: 循環参照を避けるため、gameStoreはここではimportせず、
      // 呼び出し元でswitchLocalPlayerを呼ぶか、
      // confirmTurnTransitionの後でイベントをdispatchする形にする
      console.log(`Turn transition confirmed - Switching to player: ${nextPlayerId}`);
    }

    // モーダルを閉じる
    set(state => ({
      modals: {
        ...state.modals,
        turnTransition: {
          isOpen: false,
          nextPlayerName: undefined,
          nextPlayerId: undefined
        }
      }
    }));
  },

  /**
   * 入力ブロック状態を取得
   * ターン遷移モーダルが開いている場合はブロック
   */
  isTransitionBlocking: () => {
    return get().modals.turnTransition.isOpen;
  },

  /**
   * ラウンド結果モーダルを開く
   * @param result ラウンド結果
   */
  openRoundResult: (result: RoundResult) => {
    set(state => ({
      modals: {
        ...state.modals,
        roundResult: {
          isOpen: true,
          result
        }
      }
    }));

    console.log(`Round result modal opened - Round ${result.roundNumber}, Winner: ${result.winner}`);
  },

  /**
   * ラウンド結果モーダルを閉じる
   */
  closeRoundResult: () => {
    set(state => ({
      modals: {
        ...state.modals,
        roundResult: {
          isOpen: false,
          result: undefined
        }
      }
    }));

    console.log('Round result modal closed');
  },

  /**
   * アニメーションをキューに追加
   * @param animation アニメーション情報（id、statusは自動生成）
   * @returns アニメーションID
   */
  queueAnimation: (animation: Omit<Animation, 'id' | 'status'>) => {
    const id = `anim-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newAnimation: Animation = {
      ...animation,
      id,
      status: 'pending'
    };

    set(state => ({
      animationQueue: [...state.animationQueue, newAnimation]
    }));

    console.log(`Animation queued: ${id} (type: ${animation.type})`);

    return id;
  },

  /**
   * アニメーションを開始
   * @param animationId アニメーションID
   */
  startAnimation: (animationId: string) => {
    set(state => ({
      animationQueue: state.animationQueue.map(anim =>
        anim.id === animationId
          ? { ...anim, status: 'playing' as AnimationStatus }
          : anim
      )
    }));

    console.log(`Animation started: ${animationId}`);
  },

  /**
   * アニメーションを完了
   * @param animationId アニメーションID
   */
  completeAnimation: (animationId: string) => {
    set(state => ({
      animationQueue: state.animationQueue.map(anim =>
        anim.id === animationId
          ? { ...anim, status: 'completed' as AnimationStatus }
          : anim
      )
    }));

    console.log(`Animation completed: ${animationId}`);

    // 完了したアニメーションを一定時間後にキューから削除
    setTimeout(() => {
      set(state => ({
        animationQueue: state.animationQueue.filter(anim => anim.id !== animationId)
      }));
    }, 1000);
  },

  /**
   * アニメーションキューをクリア
   */
  clearAnimationQueue: () => {
    set({ animationQueue: [] });
    console.log('Animation queue cleared');
  },

  /**
   * ペンディング中のアニメーションを取得
   * @returns ペンディング中のアニメーション配列
   */
  getPendingAnimations: () => {
    return get().animationQueue.filter(anim => anim.status === 'pending');
  },

  /**
   * トースト通知を表示
   * @param message メッセージ
   * @param type トーストタイプ
   * @param duration 表示時間（ミリ秒）デフォルト3000ms
   */
  showToast: (message: string, type: ToastType, duration: number = 3000) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const toast: Toast = {
      id,
      message,
      type,
      duration,
      timestamp: Date.now()
    };

    set(state => ({
      toasts: [...state.toasts, toast]
    }));

    console.log(`Toast shown: [${type}] ${message}`);

    // 自動消去
    setTimeout(() => {
      get().dismissToast(id);
    }, duration);
  },

  /**
   * トーストを閉じる
   * @param toastId トーストID
   */
  dismissToast: (toastId: string) => {
    set(state => ({
      toasts: state.toasts.filter(t => t.id !== toastId)
    }));

    console.log(`Toast dismissed: ${toastId}`);
  },

  /**
   * ローディング状態を設定
   * @param key ローディング状態のキー
   * @param isLoading ローディング中かどうか
   */
  setLoading: (key: keyof LoadingStates, isLoading: boolean) => {
    set(state => ({
      loadingStates: {
        ...state.loadingStates,
        [key]: isLoading
      }
    }));

    console.log(`Loading state changed: ${key} = ${isLoading}`);
  },

  /**
   * カードホバープレビューを設定
   * @param preview ホバー中のカード情報（null でクリア）
   */
  setCardHoverPreview: (preview: CardHoverPreview | null) => {
    // 同一参照のときは更新しない（subscribe loop 防止）
    const current = get().cardHoverPreview;
    if (current === preview) return;
    if (
      current &&
      preview &&
      current.cardType === preview.cardType &&
      current.drawAmount === preview.drawAmount
    ) {
      return;
    }
    set({ cardHoverPreview: preview });
  },

  /**
   * カードホバープレビューをクリア
   */
  clearCardHoverPreview: () => {
    if (get().cardHoverPreview === null) return;
    set({ cardHoverPreview: null });
  },

  /**
   * settingsStoreから設定を同期
   */
  syncPreferences: () => {
    const settingsState = useSettingsStore.getState();

    set({
      preferences: {
        soundEnabled: settingsState.audio.soundEnabled,
        animationsEnabled: settingsState.visual.animationsEnabled
      }
    });

    console.log('Preferences synced from settingsStore');
  }
}));

// settingsStoreの変更を監視して自動同期
if (typeof window !== 'undefined') {
  useSettingsStore.subscribe((_state) => {
    useUIStore.getState().syncPreferences();
  });
}

// 型エクスポート
export type {
  ScreenType,
  ToastType,
  Toast,
  AnimationType,
  AnimationStatus,
  Animation,
  AnimationData,
  PlayCardAnimationData,
  DrawCardAnimationData,
  DealCardsAnimationData,
  ModalStates,
  LoadingStates,
  UserPreferences
};
